import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Collapse, Modal } from "react-bootstrap";
import { Form } from "react-bootstrap";
import Select from "react-select";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import {
  fetchAjaxListData,
  mapAjaxRowsToOptions,
  getSupportFieldsForApp,
} from "../../utils";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BsThreeDotsVertical, BsFileEarmark } from "react-icons/bs";
import { MdKeyboardArrowDown, MdContentCopy, MdOpenInNew, MdAddCircle, MdFileDownload } from "react-icons/md";
import { FiRefreshCw } from "react-icons/fi";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import {
  Utils,
  fetchWithAuth,
  selectStyles,
  getAppOrigin,
  handleSessionExpired,
  getAppSupportConversationStatusDisplay,
  buildSupportTicketFileAcceptAttribute,
  isSupportTicketFileAllowed,
  SUPPORT_TICKET_ALLOWED_FILE_TYPES,
  getSupportContextScalar,
  filterActiveCreateTicketCustomFields,
  normalizeSupportFieldOptionRows,
  safeJsonParse,
  buildClientSupportTicketCreateUrl,
  buildClientSupportTicketPostmanPayload,
  copyToClipboard,
  getDefaultPageSize,
  can,
  getBrowserTimeZone,
  getAvatarInitials,
  nsAvatarColor,
  formatDateTime,
  TICKET_LIST_TABS,
  normalizeTicketListTab,
  supportTicketListTabStorageKey,
  getLocalStorage,
  setLocalStorage,
  exportListCsv,
} from "../../utils";
import DateRangePicker from "../common/DateRangePicker/DateRangePicker";
import { useActionDropdown } from "../../hooks/useActionDropdown";
import { useTableMaxHeight } from "../../hooks/useTableMaxHeight";
import { useScrollEndDetection } from "../../hooks/useScrollEndDetection";
import ListPaginationBar from "../common/ListPaginationBar";

const CREATE_TICKET_ERROR_INITIAL = { subject: "", message: "", requester_email: "", requester: "", form: "" };


const EMPTY_TICKET_FILTERS = {
  ticket_ref: "",
  subject: "",
  requester_customer_id: "",
  dateFrom: "",
  dateTo: "",
  datePreset: "",
  type: "",
  priority: "",
};

/** True if any accordion filter has a value (opens filter panel on load). */
function hasActiveTicketFilters(f) {
  return Object.values(f).some((v) => String(v || "").trim() !== "");
}

/** Read applied filters from URL (single source for list page). */
function filtersFromSearchParams(searchParams) {
  const legacyDate = searchParams.get("request_date") || "";
  const dateFrom = searchParams.get("date_from") || legacyDate || "";
  const dateTo = searchParams.get("date_to") || legacyDate || "";
  return {
    ticket_ref: searchParams.get("ticket_ref") || "",
    subject: searchParams.get("subject") || "",
    requester_customer_id: searchParams.get("requester_customer_id") || "",
    dateFrom,
    dateTo,
    datePreset: searchParams.get("date_preset") || "",
    type: searchParams.get("type") || "",
    priority: searchParams.get("priority") || "",
  };
}

const CREATE_TICKET_FORM_INITIAL = {
  subject: "",
  message: "",
  requester_customer_id: "",
  requester_email: "",
  requester_name: "",
  type: "problem",
  priority: "normal",
  group_id: "",
  team_member_id: "",
};

const AppSupportTicket = () => {
  const navigate = useNavigate();
  const { appId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = getDefaultPageSize();
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ ...EMPTY_TICKET_FILTERS });
  const [draftFilters, setDraftFilters] = useState({ ...EMPTY_TICKET_FILTERS });
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("recent");
  const tableScrollRef = useRef(null);
  const tableInnerRef = useRef(null);

  const tableMaxHeight = useTableMaxHeight(tableScrollRef, 240, 48, {
    deps: [filterAccordionOpen, activeTab, loading, meta.total],
  });
  const isAtScrollEnd = useScrollEndDetection(tableScrollRef, tableInnerRef, rows.length);
  const {
    openActionId,
    openActionItem,
    dropdownPosition,
    dropdownRef,
    handleDropdownAction,
    handleOpenDropdown,
  } = useActionDropdown({
    tableScrollRef,
    optionCount: () =>
      (can("support.tickets.manage") ? 1 : 0) +
      (can("support.tickets.view") ? 1 : 0),
    menuWidth: 220,
    useAnchored: true,
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createFormErrors, setCreateFormErrors] = useState(() => ({ ...CREATE_TICKET_ERROR_INITIAL }));
  const [createForm, setCreateForm] = useState({ ...CREATE_TICKET_FORM_INITIAL });
  /** { id, file, previewUrl } — previewUrl only for images; revoked on remove / modal reset */
  const [createAttachmentItems, setCreateAttachmentItems] = useState([]);
  const createAttachmentItemsRef = useRef([]);
  const createAttachmentIdRef = useRef(0);
  const createFileInputRef = useRef(null);
  const [createGroupMembers, setCreateGroupMembers] = useState([]);
  const [createCustomFieldDefs, setCreateCustomFieldDefs] = useState([]);
  const [createCustomFieldValues, setCreateCustomFieldValues] = useState({});
  const [createCustomFieldErrors, setCreateCustomFieldErrors] = useState({});
  const [createCustomersAvailable, setCreateCustomersAvailable] = useState(false);
  const [showManualRequester, setShowManualRequester] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(null);
  const [postmanPayloadSnapshot, setPostmanPayloadSnapshot] = useState(null);
  const [showPostmanPayload, setShowPostmanPayload] = useState(false);

  const clientTicketCreateUrl = useMemo(
    () => (appId ? buildClientSupportTicketCreateUrl(Utils.baseurl, appId) : ""),
    [appId]
  );

  const postmanPayload = postmanPayloadSnapshot;

  const postmanPayloadJson = useMemo(
    () => (postmanPayload ? JSON.stringify(postmanPayload, null, 2) : ""),
    [postmanPayload]
  );

  const fetchCustomerOpts = useCallback(
    ({ search, ids }) =>
      fetchAjaxListData({
        type: "support-customers",
        q: search,
        id: ids,
        extra: { app_id: appId },
        mode: "options",
      }),
    [appId]
  );
  const fetchGroupOpts = useCallback(
    ({ search, ids }) =>
      fetchAjaxListData({
        type: "support-groups",
        q: search,
        id: ids,
        extra: { app_id: appId },
        mode: "options",
      }),
    [appId]
  );
  const fetchTeamMemberOpts = useCallback(
    ({ search, ids }) =>
      fetchAjaxListData({
        type: "support-team-members",
        q: search,
        id: ids,
        extra: { app_id: appId },
        mode: "options",
      }),
    [appId]
  );

  const createAssigneeExtraOptions = useMemo(
    () => mapAjaxRowsToOptions(createGroupMembers || []),
    [createGroupMembers]
  );

  const typeOptions = useMemo(
    () => [
      { value: "", label: "Type" },
      { value: "problem", label: "Problem" },
      { value: "question", label: "Question" },
      { value: "incident", label: "Incident" },
      { value: "task", label: "Task" },
    ],
    []
  );
  const priorityOptions = useMemo(
    () => [
      { value: "", label: "Priority" },
      { value: "low", label: "Low" },
      { value: "normal", label: "Normal" },
      { value: "high", label: "High" },
      { value: "urgent", label: "Urgent" },
    ],
    []
  );

  const loadCreateCustomFields = async () => {
    if (!appId) return;
    try {
      const rows = await getSupportFieldsForApp(appId);
      const customOnly = filterActiveCreateTicketCustomFields(rows || []);
      setCreateCustomFieldDefs(customOnly);
      setCreateCustomFieldValues((prev) => {
        const next = {};
        customOnly.forEach((f) => {
          const id = String(f.field_id || "").trim();
          if (id && prev[id] !== undefined) next[id] = prev[id];
        });
        return next;
      });
    } catch {
      // modal still works without custom fields
    }
  };

  const onCreateGroupChange = async (groupId) => {
    const gid = String(groupId || "").trim();
    setCreateForm((p) => ({ ...p, group_id: gid, team_member_id: "" }));
    if (!gid) {
      setCreateGroupMembers([]);
      return;
    }
    const rows = await fetchAjaxListData({
      type: "support-groups",
      id: [gid],
      extra: { app_id: appId, with_members: 1 },
      mode: "rows",
    });
    setCreateGroupMembers((rows?.[0]?.team_members) || []);
  };

  const revokeCreateAttachmentPreviews = (items) => {
    (items || []).forEach((x) => {
      if (x?.previewUrl) URL.revokeObjectURL(x.previewUrl);
    });
  };

  const resetCreateFormFields = useCallback(() => {
    setCreateForm({ ...CREATE_TICKET_FORM_INITIAL });
    setCreateFormErrors({ ...CREATE_TICKET_ERROR_INITIAL });
    setCreateCustomFieldValues({});
    setCreateCustomFieldErrors({});
    setShowManualRequester(false);
    setCreateCustomersAvailable(false);
    setCreateAttachmentItems((prev) => {
      revokeCreateAttachmentPreviews(prev);
      return [];
    });
    if (createFileInputRef.current) createFileInputRef.current.value = "";
  }, []);

  const resetCreateModalState = useCallback(() => {
    resetCreateFormFields();
    setCreateSuccess(null);
    setPostmanPayloadSnapshot(null);
    setShowPostmanPayload(false);
  }, [resetCreateFormFields]);

  const closeCreateModal = useCallback(() => {
    if (creating) return;
    setShowCreateModal(false);
    resetCreateModalState();
  }, [creating, resetCreateModalState]);

  useEffect(() => {
    createAttachmentItemsRef.current = createAttachmentItems;
  }, [createAttachmentItems]);

  useEffect(() => {
    return () => {
      revokeCreateAttachmentPreviews(createAttachmentItemsRef.current);
    };
  }, []);

  const handleOpenCreateModal = async () => {
    resetCreateModalState();
    setShowCreateModal(true);
    loadCreateCustomFields();
    setCreateGroupMembers([]);
    try {
      const customerOpts = await fetchCustomerOpts({ search: "", ids: [] });
      const hasCustomers = customerOpts.length > 0;
      setCreateCustomersAvailable(hasCustomers);
      setShowManualRequester(!hasCustomers);
    } catch {
      setCreateCustomersAvailable(false);
      setShowManualRequester(true);
    }
  };

  const setCreateCustomFieldValue = (fieldId, value) => {
    const id = String(fieldId || "").trim();
    if (!id) return;
    setCreateCustomFieldValues((p) => ({ ...p, [id]: value }));
    setCreateCustomFieldErrors((p) => {
      if (!p[id]) return p;
      const next = { ...p };
      delete next[id];
      return next;
    });
  };

  const buildCreateCustomFieldsPayload = () => {
    const out = [];
    for (const def of createCustomFieldDefs) {
      const id = String(def.field_id || "").trim();
      if (!id) continue;
      const raw = createCustomFieldValues[id];
      if (raw === undefined || raw === null || raw === "") continue;
      const value =
        typeof raw === "boolean" ? (raw ? "true" : "false") : String(raw).trim();
      if (!value) continue;
      out.push({ id, value });
    }
    return out;
  };

  const renderCreateCustomFieldInput = (field) => {
    const fieldId = String(field.field_id || "").trim();
    const label = String(field.name || fieldId).trim();
    const fieldType = String(field.field_type || "text").trim().toLowerCase();
    const required = Number(field.is_required) === 1;
    const err = createCustomFieldErrors[fieldId] || "";
    const disabled = creating;
    const value = createCustomFieldValues[fieldId];
    const invalidClass = err ? " is-invalid" : "";

    if (fieldType === "textarea" || fieldType === "multiline") {
      return (
        <textarea
          className={`form-control${invalidClass}`}
          rows={3}
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => setCreateCustomFieldValue(fieldId, e.target.value)}
        />
      );
    }
    if (fieldType === "dropdown" || fieldType === "multi_select") {
      const opts = normalizeSupportFieldOptionRows(safeJsonParse(field.options_json, [])).map((o) => ({
        value: o.value || o.label,
        label: o.label || o.value,
      }));
      return (
        <Select
          inputId={`create-custom-${fieldId}`}
          instanceId={`create-custom-${fieldId}`}
          classNamePrefix={`ns-create-custom-${fieldId}`}
          isSearchable
          isClearable={!required}
          isDisabled={disabled}
          options={opts}
          value={opts.find((o) => o.value === value) || null}
          onChange={(opt) => setCreateCustomFieldValue(fieldId, opt?.value ?? "")}
          placeholder={`Select ${label}...`}
          styles={selectStyles}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          menuPosition="fixed"
        />
      );
    }
    if (fieldType === "checkbox") {
      return (
        <Form.Check
          type="checkbox"
          id={`create-custom-chk-${fieldId}`}
          label="Yes"
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(e) => setCreateCustomFieldValue(fieldId, e.target.checked)}
        />
      );
    }
    if (fieldType === "date") {
      return (
        <input
          type="date"
          className={`form-control${invalidClass}`}
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => setCreateCustomFieldValue(fieldId, e.target.value)}
        />
      );
    }
    if (fieldType === "number") {
      return (
        <input
          type="number"
          className={`form-control${invalidClass}`}
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => setCreateCustomFieldValue(fieldId, e.target.value)}
        />
      );
    }
    return (
      <input
        type="text"
        className={`form-control${invalidClass}`}
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => setCreateCustomFieldValue(fieldId, e.target.value)}
      />
    );
  };

  const handleCreateAttachmentInput = (e) => {
    const list = e.target.files;
    if (!list?.length) return;
    const rejected = [];
    const next = [...list]
      .filter((file) => {
        if (isSupportTicketFileAllowed(file)) return true;
        rejected.push(file.name || "file");
        return false;
      })
      .map((file) => {
        const id = ++createAttachmentIdRef.current;
        const previewUrl = /^image\//i.test(file.type) ? URL.createObjectURL(file) : null;
        return { id, file, previewUrl };
      });
    if (rejected.length) {
      toast.error(
        `Allowed types only: ${SUPPORT_TICKET_ALLOWED_FILE_TYPES.join(", ")}. Skipped: ${rejected.join(", ")}`
      );
    }
    if (next.length) setCreateAttachmentItems((prev) => [...prev, ...next]);
    e.target.value = "";
  };

  const removeCreateAttachment = (id) => {
    setCreateAttachmentItems((prev) => {
      const item = prev.find((x) => x.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter((x) => x.id !== id);
    });
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    const customerId = createForm.requester_customer_id.trim();
    const emailTrim = createForm.requester_email.trim();
    const useManualRequester = showManualRequester || !createCustomersAvailable;
    const errs = { ...CREATE_TICKET_ERROR_INITIAL };
    const customErrs = {};
    if (!createForm.subject.trim()) errs.subject = "Subject is required";
    if (!createForm.message.trim()) errs.message = "Message is required";
    if (useManualRequester) {
      if (!emailTrim) errs.requester_email = "Requester email is required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
        errs.requester_email = "Enter a valid email address";
      }
    } else if (!customerId) {
      errs.requester = "Select a customer or click Add New to enter email";
    }
    for (const def of createCustomFieldDefs) {
      const id = String(def.field_id || "").trim();
      if (!id || Number(def.is_required) !== 1) continue;
      const raw = createCustomFieldValues[id];
      const empty =
        raw === undefined ||
        raw === null ||
        raw === "" ||
        (typeof raw === "boolean" && !raw);
      if (empty) customErrs[id] = `${String(def.name || id).trim()} is required`;
    }
    if (errs.subject || errs.message || errs.requester_email || errs.requester || Object.keys(customErrs).length) {
      setCreateFormErrors(errs);
      setCreateCustomFieldErrors(customErrs);
      return;
    }
    setCreateFormErrors({ ...CREATE_TICKET_ERROR_INITIAL });
    setCreateCustomFieldErrors({});
    setCreating(true);
    try {
      const fieldsPayload = buildCreateCustomFieldsPayload();
      const fd = new FormData();
      fd.append("app_id", appId);
      const panelOrigin = getAppOrigin();
      if (panelOrigin) fd.append("origin", panelOrigin);
      fd.append("subject", createForm.subject.trim());
      fd.append("message", createForm.message.trim());
      if (!useManualRequester && customerId) {
        fd.append("requester_customer_id", customerId);
      } else {
        fd.append("requester_email", createForm.requester_email.trim());
        if (createForm.requester_name.trim()) fd.append("requester_name", createForm.requester_name.trim());
      }
      if (createForm.type) fd.append("type", createForm.type);
      if (createForm.priority) fd.append("priority", createForm.priority);
      if (createForm.group_id) fd.append("group_id", createForm.group_id);
      if (createForm.team_member_id) fd.append("team_member_id", createForm.team_member_id);
      if (fieldsPayload.length) fd.append("fields", JSON.stringify(fieldsPayload));
      createAttachmentItems.forEach((item) => fd.append("attachments", item.file));
      const res = await fetchWithAuth(`${Utils.baseurl}application/support/create-ticket`, { method: "POST", body: fd });
      const result = await res.json().catch(() => ({}));
      if (handleSessionExpired(res, result)) return;
      if (!result.status) {
        setCreateFormErrors((prev) => ({
          ...CREATE_TICKET_ERROR_INITIAL,
          form: result.message || "Failed to create ticket",
        }));
        return;
      }
      if (result.emailSent === false) {
        toast.warn(
          result.message ||
            "Ticket created but the assignee notification email could not be sent."
        );
      } else {
        toast.success("Ticket created successfully");
      }
      setPostmanPayloadSnapshot(
        buildClientSupportTicketPostmanPayload(
          createForm,
          createCustomFieldValues,
          createCustomFieldDefs,
          createAttachmentItems
        )
      );
      setCreateSuccess({
        conversationId: result.conversationId,
        ticket_ref: result.ticket_ref,
      });
      setShowPostmanPayload(false);
      loadData(1, filters);
      const ticketRefLabel = result.ticket_ref ? ` (${result.ticket_ref})` : "";
      const { isConfirmed } = await Swal.fire({
        title: "Ticket created",
        text: `Ticket created successfully${ticketRefLabel}. Do you want to open this ticket's details page?`,
        icon: "success",
        showCancelButton: true,
        confirmButtonColor: "#0d6efd",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, open ticket",
        cancelButtonText: "No",
      });
      if (isConfirmed) {
        handleOpenCreatedTicket(result.conversationId);
      }
    } catch {
      setCreateFormErrors((prev) => ({
        ...CREATE_TICKET_ERROR_INITIAL,
        form: "Network error while creating ticket",
      }));
    } finally {
      setCreating(false);
    }
  };

  const handleOpenCreatedTicket = (conversationId) => {
    const id = conversationId ?? createSuccess?.conversationId;
    closeCreateModal();
    if (id) {
      navigate(`/apps/${appId}/support/ticket/details/${id}`);
    }
  };

  const handleCopyPostman = async (text, label) => {
    await copyToClipboard(text);
    toast.success(`${label} copied`);
  };

  const loadData = async (nextPage = page, nextFilters = filters, tab = activeTab) => {
    if (!appId) return;
    setLoading(true);
    fetchWithAuth(`${Utils.baseurl}application/support/conversations`, {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        tab,
        page: nextPage,
        pageSize,
        ticket_ref: (nextFilters.ticket_ref || "").trim(),
        subject: (nextFilters.subject || "").trim(),
        requester_customer_id: (nextFilters.requester_customer_id || "").trim(),
        date_from: (nextFilters.dateFrom || "").trim() || undefined,
        date_to: (nextFilters.dateTo || "").trim() || undefined,
        timezone: getBrowserTimeZone() || undefined,
        type: (nextFilters.type || "").trim(),
        priority: (nextFilters.priority || "").trim(),
      }),
    })
      .then(async (res) => {
        const result = await res.json().catch(() => ({}));
        setLoading(false);
        if (handleSessionExpired(res, result)) return;
        if (!result.status) {
          toast.error(result.message || "Failed to load tickets");
          return;
        }
        const pagedRows = result.data || [];
        const m = result.meta || {};
        setRows(pagedRows);
        setMeta({
          page: m.page || nextPage,
          pageSize: m.pageSize || pageSize,
          total: m.total ?? pagedRows.length,
          totalPages: m.totalPages || 1,
        });
        setPage(m.page || nextPage);
      })
      .catch(() => {
        setLoading(false);
        toast.error("Network error while loading tickets");
      });
  };

  useEffect(() => {
    if (!appId) return;
    const rawTab = searchParams.get("tab");
    const hasUrlTab = rawTab != null && String(rawTab).trim() !== "";
    const tab = hasUrlTab
      ? normalizeTicketListTab(rawTab)
      : normalizeTicketListTab(getLocalStorage(supportTicketListTabStorageKey(appId)));
    setLocalStorage(supportTicketListTabStorageKey(appId), tab);

    if (!hasUrlTab && tab !== "recent") {
      const sp = new URLSearchParams(searchParams);
      sp.set("tab", tab);
      setSearchParams(sp, { replace: true });
      return;
    }

    const p = Math.max(1, parseInt(searchParams.get("page") || "1", 10));
    const f = filtersFromSearchParams(searchParams);
    setActiveTab(tab);
    setPage(p);
    setFilters(f);
    setDraftFilters(f);
    setFilterAccordionOpen(hasActiveTicketFilters(f));
    loadData(p, f, tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, searchParams]);

  const handleTabChange = (tabKey) => {
    setLocalStorage(supportTicketListTabStorageKey(appId), normalizeTicketListTab(tabKey));
    setActiveTab(tabKey);
    setPage(1);
    const sp = new URLSearchParams(searchParams);
    if (tabKey === "recent") sp.delete("tab");
    else sp.set("tab", tabKey);
    sp.delete("page");
    setSearchParams(sp, { replace: true });
  };

  const writeParams = (p, f, tab = activeTab) => {
    const sp = new URLSearchParams();
    if (tab && tab !== "recent") sp.set("tab", tab);
    if (p > 1) sp.set("page", String(p));
    if ((f.ticket_ref || "").trim()) sp.set("ticket_ref", f.ticket_ref.trim());
    if ((f.subject || "").trim()) sp.set("subject", f.subject.trim());
    if ((f.requester_customer_id || "").trim()) sp.set("requester_customer_id", f.requester_customer_id.trim());
    if ((f.dateFrom || "").trim()) sp.set("date_from", f.dateFrom.trim());
    if ((f.dateTo || "").trim()) sp.set("date_to", f.dateTo.trim());
    if ((f.datePreset || "").trim()) sp.set("date_preset", f.datePreset.trim());
    if ((f.type || "").trim()) sp.set("type", f.type.trim());
    if ((f.priority || "").trim()) sp.set("priority", f.priority.trim());
    setSearchParams(sp, { replace: true });
  };

  const onApplyFilters = () => {
    const f = { ...draftFilters };
    setFilters(f);
    setDraftFilters(f);
    setPage(1);
    writeParams(1, f, activeTab);
    setFilterAccordionOpen(hasActiveTicketFilters(f));
  };

  const onResetFilters = () => {
    const f = { ...EMPTY_TICKET_FILTERS };
    setDraftFilters(f);
    setFilters(f);
    setPage(1);
    writeParams(1, f, activeTab);
    setFilterAccordionOpen(false);
  };

  const handleExport = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const { status, message } = await exportListCsv("application/support/conversations", {
        app_id: appId,
        tab: activeTab,
        ticket_ref: (filters.ticket_ref || "").trim(),
        subject: (filters.subject || "").trim(),
        requester_customer_id: (filters.requester_customer_id || "").trim(),
        date_from: (filters.dateFrom || "").trim() || undefined,
        date_to: (filters.dateTo || "").trim() || undefined,
        timezone: getBrowserTimeZone() || undefined,
        type: (filters.type || "").trim(),
        priority: (filters.priority || "").trim(),
      }, "tickets.csv");
      if (!status) {
        toast.error(message);
        return;
      }
      if (message) toast.info(message);
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_inner ns-support-list">
        <div className="dashboard_right_top">
          <div className="dashboard_heading">
            <h1>Tickets</h1>
          </div>
          <div className="add_app" style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Refresh list" onClick={() => loadData(page, filters, activeTab)}>
              <FiRefreshCw />
              <span>Refresh</span>
            </button>
            <button type="button" className="add_app_btn_secondary btn_theme_secondary" title="Export CSV for applied filters" onClick={handleExport} disabled={isExporting}>
              <MdFileDownload />
              <span>{isExporting ? "Exporting…" : "Export"}</span>
            </button>
            {can("support.tickets.manage") ? (
              <button type="button" className="add_app_btn btn_theme_primary" onClick={handleOpenCreateModal}>
                <MdAddCircle />
                <span>Create Ticket</span>
              </button>
            ) : null}
          </div>
        </div>

        <div className="list-page-filter-card">
          <button
            type="button"
            className="list-page-filter-accordion-header"
            onClick={() => setFilterAccordionOpen((o) => !o)}
            aria-expanded={filterAccordionOpen}
          >
            <span className="list-page-filter-accordion-title">Filter</span>
            <MdKeyboardArrowDown className={`list-page-filter-accordion-chevron ${filterAccordionOpen ? "list-page-filter-accordion-chevron--open" : ""}`} size={22} />
          </button>
          <Collapse in={filterAccordionOpen}>
            <div className="list-page-filter-accordion-body">
              <div className="list-page-filter-bar">
                <div className="list-page-filter-fields row g-3 align-items-end">
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <input className="form-control" placeholder="Ticket id" value={draftFilters.ticket_ref} onChange={(e) => setDraftFilters((p) => ({ ...p, ticket_ref: e.target.value }))} />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3 d-none">
                    <input className="form-control" placeholder="Subject" value={draftFilters.subject} onChange={(e) => setDraftFilters((p) => ({ ...p, subject: e.target.value }))} />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <AsyncSearchSelect
                      inputId="support-ticket-filter-customer"
                      instanceId="support-ticket-filter-customer"
                      classNamePrefix="ns-support-ticket-filter-customer"
                      placeholder="Customer"
                      isClearable
                      fetchOptions={fetchCustomerOpts}
                      value={draftFilters.requester_customer_id || null}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, requester_customer_id: opt?.value ?? "" }))}
                      styles={selectStyles}
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <DateRangePicker
                      dateFrom={draftFilters.dateFrom || ""}
                      dateTo={draftFilters.dateTo || ""}
                      preset={draftFilters.datePreset || ""}
                      onChange={({ dateFrom, dateTo, preset }) =>
                        setDraftFilters((p) => ({
                          ...p,
                          dateFrom: dateFrom || "",
                          dateTo: dateTo || "",
                          datePreset: preset || "",
                        }))
                      }
                      label=""
                      placeholder="Created date"
                      allowClear
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3 d-none">
                    <Select
                      inputId="support-ticket-filter-type"
                      instanceId="support-ticket-filter-type"
                      classNamePrefix="ns-support-ticket-filter-type"
                      isSearchable
                      options={typeOptions}
                      value={typeOptions.find((o) => o.value === String(draftFilters.type || "")) || typeOptions[0]}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, type: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field col-12 col-lg-3">
                    <Select
                      inputId="support-ticket-filter-priority"
                      instanceId="support-ticket-filter-priority"
                      classNamePrefix="ns-support-ticket-filter-priority"
                      isSearchable
                      options={priorityOptions}
                      value={priorityOptions.find((o) => o.value === String(draftFilters.priority || "")) || priorityOptions[0]}
                      onChange={(opt) => setDraftFilters((p) => ({ ...p, priority: opt?.value ?? "" }))}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                  <div className="list-page-filter-field list-page-filter-actions col-12 col-lg-12">
                    <div className="add_app">
                      <button type="button" className="add_app_btn btn_theme_primary" onClick={onApplyFilters}><span>Search</span></button>
                      <button type="button" className="add_app_btn_secondary btn_theme_secondary" onClick={onResetFilters}><span>Reset</span></button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Collapse>
        </div>

        <div className="ns-tabs">
          {TICKET_LIST_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`ns-tab ${activeTab === tab.key ? "ns-tab--active" : ""}`}
              onClick={() => handleTabChange(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="dashboard_app_table dashboard_app_table--list-records">
          <div className="dashboard_table">
            <div ref={tableScrollRef} className="table_scroll_wrapper">
              <div
                ref={tableInnerRef}
                className={`table_inner table_y_scroll ${isAtScrollEnd ? "at-scroll-end" : ""}`}
                style={{
                  maxHeight: tableMaxHeight ? `${tableMaxHeight}px` : "calc(100vh - 380px)",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table_outer table-sm">
                  <thead>
                    <tr>
                      <th>Requester</th>
                      <th>Subject</th>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th className="col-action" style={{ textAlign: "right" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan="7" className="text-center">Loading...</td></tr>
                    ) : rows.length === 0 ? (
                      <tr><td colSpan="7" className="text-center">No tickets found</td></tr>
                    ) : rows.map((conv) => (
                      <tr
                        key={conv.id}
                        className="support-ticket-row--clickable"
                        onClick={() => navigate(`/apps/${appId}/support/ticket/details/${conv.id}`)}
                      >
                        {(() => {
                          const type = getSupportContextScalar(conv.context_json, ["type"]) || "-";
                          const priority = getSupportContextScalar(conv.context_json, ["priority"]) || "-";
                          const reqDate = conv.createdAt ? formatDateTime(conv.createdAt) : "-";
                          const statusDisplay = getAppSupportConversationStatusDisplay(conv.status);
                          return (
                            <>
                              <td>
                                {(() => {
                                  const rName = conv.requester_name || conv.app_user?.applicationUserId || "App User";
                                  const c = nsAvatarColor(conv.requester_email || rName);
                                  return (
                                    <div className="ns-requester">
                                      <span className="ns-avatar" style={{ background: c.bg, color: c.text }}>
                                        {getAvatarInitials(rName)}
                                      </span>
                                      <div style={{ minWidth: 0 }}>
                                        <div className="ns-requester-name">{rName}</div>
                                        <div className="ns-requester-email">{conv.requester_email || "-"}</div>
                                      </div>
                                    </div>
                                  );
                                })()}
                              </td>
                              <td style={{ maxWidth: 360 }}>{conv.subject || "-"}</td>
                              <td>{reqDate}</td>
                              <td>{type && type !== "-" ? <span className="ns-pill">{type}</span> : "-"}</td>
                              <td>{priority && priority !== "-" ? <span className="ns-pill">{priority}</span> : "-"}</td>
                              <td>
                                <span className={statusDisplay.badgeClass}>
                                  {statusDisplay.label}
                                </span>
                              </td>
                              <td
                                className="col-action"
                                style={{ textAlign: "right" }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="support-actions-menu" style={{ position: "relative", display: "inline-block" }}>
                                  <button
                                    type="button"
                                    className="dropdown-trigger-btn btn btn-link p-0"
                                    aria-label="Actions"
                                    aria-expanded={openActionId === conv.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenDropdown(e, conv.id, conv);
                                    }}
                                  >
                                    <BsThreeDotsVertical />
                                  </button>
                                </div>
                              </td>
                            </>
                          );
                        })()}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPaginationBar
                page={page}
                setPage={(n) => writeParams(n, filters, activeTab)}
                total={meta.total || 0}
                pageSize={pageSize}
                totalPages={meta.totalPages || 1}
                ariaLabel="Support tickets pagination"
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
      {openActionItem && dropdownPosition
        ? createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              left: dropdownPosition.left,
              top: dropdownPosition.top,
              zIndex: 999999,
              minWidth: "220px",
            }}
          >
            <div className="dropdown-menu-subscription">
              {can("support.tickets.manage") ? (
                <button
                  type="button"
                  onClick={() =>
                    handleDropdownAction(() => navigate(`/apps/${appId}/support/ticket/details/${openActionItem.id}`))
                  }
                  style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                >
                  <MdOpenInNew size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                  <span style={{ fontSize: "0.875rem" }}>Open Ticket</span>
                </button>
              ) : null}
              {can("support.tickets.view") ? (
                <button
                  type="button"
                  onClick={() =>
                    handleDropdownAction(async () => {
                      const ticketId = openActionItem.ticket_ref || `NS-SUP-${openActionItem.id}`;
                      await copyToClipboard(ticketId);
                      toast.success("Ticket ID copied");
                    })
                  }
                  style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", textAlign: "left" }}
                >
                  <MdContentCopy size={18} style={{ marginRight: "8px", flexShrink: 0 }} />
                  <span style={{ fontSize: "0.875rem" }}>Copy Ticket ID</span>
                </button>
              ) : null}
            </div>
          </div>,
          document.body
        )
        : null}

      <Modal
        show={showCreateModal}
        onHide={closeCreateModal}
        size="lg"
        centered
        className="ns-modal"
        dialogClassName="create-ticket-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Ticket</Modal.Title>
        </Modal.Header>
        <form noValidate onSubmit={handleCreateTicket} className="create-ticket-modal-form">
          <Modal.Body>
            {createFormErrors.form ? (
              <div className="alert alert-danger py-2 mb-3" role="alert">
                {createFormErrors.form}
              </div>
            ) : null}
            <div className="row g-3">
              <div className="col-12">
                <div className="form_group">
                  <label>Subject *</label>
                  <input
                    className={`form-control${createFormErrors.subject ? " is-invalid" : ""}`}
                    placeholder="Enter subject"
                    value={createForm.subject}
                    onChange={(e) => {
                      setCreateForm((p) => ({ ...p, subject: e.target.value }));
                      setCreateFormErrors((p) => ({ ...p, subject: "", form: "" }));
                    }}
                    disabled={creating}
                    aria-invalid={createFormErrors.subject ? "true" : "false"}
                  />
                  {createFormErrors.subject ? (
                    <div className="invalid-feedback d-block" style={{ marginTop: "0.25rem" }}>
                      {createFormErrors.subject}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="col-12">
                <div className="form_group">
                  <label>Message *</label>
                  <textarea
                    className={`form-control${createFormErrors.message ? " is-invalid" : ""}`}
                    rows={4}
                    placeholder="Describe the issue..."
                    value={createForm.message}
                    onChange={(e) => {
                      setCreateForm((p) => ({ ...p, message: e.target.value }));
                      setCreateFormErrors((p) => ({ ...p, message: "", form: "" }));
                    }}
                    disabled={creating}
                    aria-invalid={createFormErrors.message ? "true" : "false"}
                  />
                  {createFormErrors.message ? (
                    <div className="invalid-feedback d-block" style={{ marginTop: "0.25rem" }}>
                      {createFormErrors.message}
                    </div>
                  ) : null}
                </div>
              </div>
              {createCustomersAvailable ? (
                <div className="col-12">
                  <div className="form_group">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                      <label className="mb-0">Customer *</label>
                      {!showManualRequester ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          disabled={creating}
                          onClick={() => {
                            setShowManualRequester(true);
                            setCreateForm((p) => ({ ...p, requester_customer_id: "" }));
                            setCreateFormErrors((prev) => ({ ...prev, requester: "", requester_email: "", form: "" }));
                          }}
                        >
                          Add New
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0"
                          disabled={creating}
                          onClick={() => {
                            setShowManualRequester(false);
                            setCreateForm((p) => ({ ...p, requester_email: "", requester_name: "" }));
                            setCreateFormErrors((prev) => ({ ...prev, requester: "", requester_email: "", form: "" }));
                          }}
                        >
                          Use existing customer
                        </button>
                      )}
                    </div>
                    {!showManualRequester ? (
                      <>
                        <AsyncSearchSelect
                          inputId="create-ticket-customer"
                          instanceId="create-ticket-customer"
                          classNamePrefix="ns-create-ticket-customer"
                          placeholder="Search customer…"
                          isClearable
                          isDisabled={creating}
                          fetchOptions={fetchCustomerOpts}
                          value={createForm.requester_customer_id || null}
                          onChange={(opt) => {
                            setCreateForm((p) => ({ ...p, requester_customer_id: opt?.value ?? "" }));
                            setCreateFormErrors((p) => ({ ...p, requester: "", form: "" }));
                          }}
                          styles={selectStyles}
                          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                          menuPosition="fixed"
                        />
                        {createFormErrors.requester ? (
                          <div className="invalid-feedback d-block" style={{ marginTop: "0.25rem" }}>
                            {createFormErrors.requester}
                          </div>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {(showManualRequester || !createCustomersAvailable) && (
              <>
              <div className="col-12 col-md-6">
                <div className="form_group">
                  <label>Requester Email *</label>
                  <input
                    type="email"
                    autoComplete="email"
                    className={`form-control${createFormErrors.requester_email ? " is-invalid" : ""}`}
                    placeholder="requester@example.com"
                    value={createForm.requester_email}
                    onChange={(e) => {
                      setCreateForm((p) => ({ ...p, requester_email: e.target.value }));
                      setCreateFormErrors((p) => ({ ...p, requester_email: "", form: "" }));
                    }}
                    disabled={creating}
                    aria-invalid={createFormErrors.requester_email ? "true" : "false"}
                  />
                  {createFormErrors.requester_email ? (
                    <div className="invalid-feedback d-block" style={{ marginTop: "0.25rem" }}>
                      {createFormErrors.requester_email}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form_group">
                  <label>Requester Name</label>
                  <input
                    className="form-control"
                    placeholder="Name (optional)"
                    value={createForm.requester_name}
                    onChange={(e) => setCreateForm((p) => ({ ...p, requester_name: e.target.value }))}
                    disabled={creating}
                  />
                </div>
              </div>
              </>
              )}
              <div className="col-12 col-md-6">
                <div className="form_group">
                  <label>Type</label>
                  <Select
                    inputId="create-ticket-type"
                    instanceId="create-ticket-type"
                    classNamePrefix="ns-create-ticket-type"
                    isSearchable
                    isDisabled={creating}
                    options={typeOptions.filter((o) => o.value !== "")}
                    value={typeOptions.find((o) => o.value === createForm.type) || typeOptions.find((o) => o.value === "problem")}
                    onChange={(opt) => setCreateForm((p) => ({ ...p, type: opt?.value ?? "problem" }))}
                    placeholder="Select type..."
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form_group">
                  <label>Priority</label>
                  <Select
                    inputId="create-ticket-priority"
                    instanceId="create-ticket-priority"
                    classNamePrefix="ns-create-ticket-priority"
                    isSearchable
                    isDisabled={creating}
                    options={priorityOptions.filter((o) => o.value !== "")}
                    value={priorityOptions.find((o) => o.value === createForm.priority) || priorityOptions.find((o) => o.value === "normal")}
                    onChange={(opt) => setCreateForm((p) => ({ ...p, priority: opt?.value ?? "normal" }))}
                    placeholder="Select priority..."
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form_group">
                  <label>Group</label>
                  <AsyncSearchSelect
                    inputId="create-ticket-group"
                    instanceId="create-ticket-group"
                    classNamePrefix="ns-create-ticket-group"
                    isDisabled={creating}
                    fetchOptions={fetchGroupOpts}
                    value={createForm.group_id || null}
                    onChange={(opt) => onCreateGroupChange(opt?.value ?? "")}
                    placeholder="Type to search groups…"
                    styles={selectStyles}
                  />
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="form_group">
                  <label>Team Member</label>
                  {createForm.group_id ? (
                    <Select
                      inputId="create-ticket-assignee"
                      instanceId="create-ticket-assignee"
                      classNamePrefix="ns-create-ticket-assignee"
                      isSearchable
                      isDisabled={creating || !createAssigneeExtraOptions.length}
                      options={createAssigneeExtraOptions}
                      value={createAssigneeExtraOptions.find((o) => o.value === createForm.team_member_id) || null}
                      onChange={(opt) => setCreateForm((p) => ({ ...p, team_member_id: opt?.value ?? "" }))}
                      placeholder={createAssigneeExtraOptions.length ? "Select team member…" : "No members in group"}
                      isClearable
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                    />
                  ) : (
                    <AsyncSearchSelect
                      inputId="create-ticket-assignee"
                      instanceId="create-ticket-assignee"
                      classNamePrefix="ns-create-ticket-assignee"
                      isDisabled={creating}
                      fetchOptions={fetchTeamMemberOpts}
                      value={createForm.team_member_id || null}
                      onChange={(opt) => setCreateForm((p) => ({ ...p, team_member_id: opt?.value ?? "" }))}
                      placeholder="Type to search team members…"
                      styles={selectStyles}
                    />
                  )}
                </div>
              </div>
              {createCustomFieldDefs.length > 0
                ? createCustomFieldDefs.map((field) => {
                    const fieldId = String(field.field_id || "").trim();
                    const label = String(field.name || fieldId).trim();
                    const required = Number(field.is_required) === 1;
                    const err = createCustomFieldErrors[fieldId] || "";
                    return (
                      <div className="col-12 col-md-6" key={fieldId}>
                        <div className="form_group">
                          <label>
                            {label}
                            {required ? " *" : null}
                          </label>
                          {renderCreateCustomFieldInput(field)}
                          {err ? (
                            <div className="invalid-feedback d-block" style={{ marginTop: "0.25rem" }}>
                              {err}
                            </div>
                          ) : null}
                        </div>
                      </div>
                    );
                  })
                : null}
              <div className="col-12">
                <div className="form_group">
                  <label>Attachments</label>
                  <input
                    ref={createFileInputRef}
                    type="file"
                    className="d-none"
                    multiple
                    accept={buildSupportTicketFileAcceptAttribute()}
                    onChange={handleCreateAttachmentInput}
                    disabled={creating}
                  />
                  <button
                    type="button"
                    className="btn_theme_primary"
                    style={{ padding: "0.375rem 0.75rem", fontSize: "0.875rem" }}
                    disabled={creating}
                    onClick={() => createFileInputRef.current?.click()}
                  >
                    Choose File
                  </button>
                  <small className="text-muted d-block mt-2">
                    Max 10MB per file. Allowed: jpeg, jpg, png, gif, pdf, doc, docx, txt, csv, zip, json, webp
                  </small>
                  {createAttachmentItems.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mt-3" style={{ maxWidth: "100%" }}>
                      {createAttachmentItems.map((item) => {
                        const name = item.file?.name || "file";
                        const ext = (name.split(".").pop() || "").toLowerCase();
                        const isPdf = item.file?.type === "application/pdf" || ext === "pdf";
                        return (
                          <div
                            key={item.id}
                            className="position-relative border rounded bg-white shadow-sm"
                            style={{ width: 88, height: 88, overflow: "hidden" }}
                          >
                            {item.previewUrl ? (
                              <img alt="" src={item.previewUrl} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : (
                              <div className="d-flex flex-column align-items-center justify-content-center h-100 p-1 small text-center" style={{ fontSize: 10 }}>
                                {isPdf ? (
                                  <span className="text-danger fw-bold" style={{ fontSize: 22 }}>PDF</span>
                                ) : (
                                  <BsFileEarmark className="text-secondary" style={{ fontSize: 28 }} aria-hidden />
                                )}
                                <span className="text-truncate w-100" title={name}>{name}</span>
                              </div>
                            )}
                            <button
                              type="button"
                              className="position-absolute top-0 end-0 m-1 btn btn-danger rounded-circle p-0 d-flex align-items-center justify-content-center"
                              style={{ width: 22, height: 22, fontSize: 12, lineHeight: 1 }}
                              aria-label={`Remove ${name}`}
                              onClick={() => removeCreateAttachment(item.id)}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {createSuccess ? (
              <div className="create-ticket-postman mt-3 pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-link p-0 text-decoration-none"
                  style={{ fontSize: "0.875rem" }}
                  onClick={() => setShowPostmanPayload((v) => !v)}
                  aria-expanded={showPostmanPayload}
                >
                  {showPostmanPayload ? "Hide Postman payload" : "View Postman payload"}
                </button>
                <Collapse in={showPostmanPayload}>
                  <div className="mt-2">
                    <div className="mb-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <small className="text-muted fw-semibold">Client API URL</small>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleCopyPostman(clientTicketCreateUrl, "URL")}
                        >
                          Copy URL
                        </button>
                      </div>
                      <code className="d-block p-2 bg-light rounded small text-break">{clientTicketCreateUrl}</code>
                      <small className="text-muted d-block mt-1">
                        POST with multipart or JSON. Header: <code>Authorization: Bearer &lt;app_api_token&gt;</code>
                      </small>
                    </div>
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <small className="text-muted fw-semibold">API request body (client API format)</small>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => handleCopyPostman(postmanPayloadJson, "JSON")}
                        >
                          Copy JSON
                        </button>
                      </div>
                      <pre
                        className="p-2 bg-light rounded small mb-0"
                        style={{ maxHeight: 280, overflow: "auto", fontSize: "0.75rem" }}
                      >
                        {postmanPayloadJson}
                      </pre>
                    </div>
                  </div>
                </Collapse>
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn_theme_secondary" style={{ marginRight: "8px" }} onClick={closeCreateModal} disabled={creating}>
              Cancel
            </button>
            {can("support.tickets.manage") ? (
              <button type="submit" className="add_app_btn btn_theme_primary" disabled={creating}>
                <span>{creating ? "Creating..." : "Create"}</span>
              </button>
            ) : null}
          </Modal.Footer>
        </form>
      </Modal>

    </div>
  );
};

export default AppSupportTicket;
